/**
 * The onboarding flow's shape: its screens, the progress rail, and the fixture data the
 * prototype supplies.
 *
 * Every number, name and tenant id in here is fixture data from the design handoff — "the shape
 * of the real data, not content", in its words. Step 5 of the build replaces the fixtures with
 * the API; the screen sequence and copy stay.
 */
import type { ComponentType } from 'react'

import {
  GoogleMark,
  MicrosoftMark,
  OktaMark,
  type VendorMarkProps,
} from '../ui/VendorMark'

export type Screen =
  | 'landing'
  | 'signIn'
  | 'magicLinkSent'
  | 'welcome'
  | 'permissions'
  | 'consent'
  | 'adminApproval'
  | 'connecting'
  | 'connected'
  | 'assessing'
  | 'result'
  | 'dashboard'

export type ProviderId = 'entra' | 'google' | 'okta'

/**
 * How the person got in, phrased for the "Signed in with" row.
 *
 * `null` means no SSO provider, which means a magic link -- that one is a real answer rather than
 * a fallback, so it is handled on its own.
 *
 * Everything else is exhaustive over ProviderId. It was `default: return 'Magic link'`, which
 * meant a provider added later would tell a customer they had signed in with a magic link when
 * they had not -- a small wrongness on a row about their own authentication, which is not a row to
 * be casually wrong on. The same shape as the consent bug, at lower stakes.
 */
export function describeAuthMethod(provider: ProviderId | null): string {
  if (provider === null) return 'Magic link'

  switch (provider) {
    case 'entra':
      return 'Microsoft Entra ID SSO'
    case 'google':
      return 'Google Workspace SSO'
    case 'okta':
      return 'Okta SSO (SAML)'
    default:
      return assertNeverProvider(provider)
  }
}

/** Fails the build when a ProviderId is added without being described. */
function assertNeverProvider(provider: never): string {
  return `Unknown (${String(provider)})`
}

export interface Provider {
  id: ProviderId
  name: string
  note: string
  protocol: string

  /**
   * The vendor's mark, as a component rather than a string.
   *
   * A component because the marks are inline SVG: an `<img src>` costs a request and lands after
   * first paint, so all three buttons reflow as the logos arrive on the first screen a customer
   * sees. See `ui/VendorMark.tsx` — the real assets are not in the repository yet, so these
   * currently render the same letters they always did.
   */
  Mark: ComponentType<VendorMarkProps>
  /**
   * False for providers that are designed but not built.
   *
   * The prototype shows them disabled, so the design's three providers stay visible to whoever
   * builds the rest. The real sign-in page does not show them at all (AVAILABLE_PROVIDERS): two
   * greyed-out buttons carrying traced placeholder vendor marks were the only unfinished thing on
   * the first screen a customer sees, and "Not available yet" answers no question a customer asked.
   */
  available: boolean
}

export const PROVIDERS: Provider[] = [
  {
    id: 'entra',
    name: 'Microsoft Entra ID',
    // Not "Recommended": a Microsoft sign-in cannot start an account. Only a mailbox creates one (migration
    // 0104; AuthEndpoints refuses an unlinked identity with entra_not_linked), and the person attaches
    // their Microsoft account afterwards, in Settings. So this button is for accounts that have done that,
    // and a first-time visitor who was told it was the recommended way in was sent to an error page.
    note: 'For accounts that already have a Microsoft account connected',
    protocol: 'OIDC',
    Mark: MicrosoftMark,
    available: true,
  },
  {
    id: 'google',
    name: 'Google Workspace',
    note: 'For teams whose identity lives in Google',
    protocol: 'OIDC',
    Mark: GoogleMark,
    available: false,
  },
  {
    id: 'okta',
    name: 'Okta',
    note: 'Your Okta org signs in through SAML or OIDC',
    protocol: 'SAML',
    Mark: OktaMark,
    available: false,
  },
]

/** The providers that actually sign someone in: what the production sign-in page offers. */
export const AVAILABLE_PROVIDERS: Provider[] = PROVIDERS.filter((provider) => provider.available)

/** The four-step rail under the nav. */
export const RAIL = [
  'Account created',
  'Connect Microsoft 365',
  'Security assessment',
  'Security dashboard',
] as const

/**
 * Which rail step each screen sits at.
 *
 * The landing and the Microsoft consent screen are absent on purpose: neither shows the rail.
 * Consent is a Microsoft-owned surface in production, and drawing our own progress furniture on
 * it would misrepresent whose page the customer is looking at.
 */
export const RAIL_STEP: Record<Screen, number | null> = {
  landing: null,
  signIn: 0,
  magicLinkSent: 0,
  welcome: 1,
  permissions: 1,
  consent: null,
  adminApproval: 1,
  connecting: 1,
  connected: 1,
  assessing: 2,
  result: 2,
  dashboard: 3,
}

/**
 * The services checked off while the connection is verified, at 480ms each (prototype only).
 *
 * Purview and Sentinel were on this list, ticked as "connected". Defenssive connects to neither:
 * Purview's reads are not registered and Sentinel is not reached at all.
 */
export const CONNECTION_CHECKS = [
  'Microsoft Entra ID',
  'Microsoft Graph',
  'Defender APIs',
  'Microsoft 365',
  'Intune',
]

/** The eight areas the first assessment covers, checked off as the percentage passes each. */
export const ASSESSMENT_AREAS = [
  'Identity Security',
  'Endpoint Security',
  'Email Security',
  'Microsoft 365 Security',
  'Cloud Security',
  'Data Security',
  'Compliance',
  'Security Operations',
]

/**
 * The permissions shown on the consent screen.
 *
 * These are the eleven Microsoft Graph application permissions the app registration requests
 * (scripts/create-entra-app.ps1 $APPLICATION). The registration's Defender and Exchange
 * permissions are not listed on this simulated screen. If the Graph lists ever diverge, this
 * list is the lie — the consent screen in production is Microsoft's, rendered from the
 * registration, and a customer who read a different list here would be right to distrust
 * everything after it.
 */
export const REQUESTED_SCOPES = [
  // THE ACTUAL LIST, verified against the app registration rather than written from memory.
  // This named three permissions the registration has never requested -- ThreatHunting,
  // IdentityRiskyUser and InformationProtectionPolicy -- and omitted four it does. An
  // administrator reading this screen was being told something Microsoft's own consent dialogue
  // would then contradict, on the page whose entire job is saying what is about to be granted.
  // It then kept SecurityIncident.Read.All and DeviceManagementConfiguration.Read.All after both
  // were removed from the registration on 2026-09-07 (nothing reads either).
  'Directory.Read.All',
  'Policy.Read.All',
  'RoleManagement.Read.Directory',
  'AuditLog.Read.All',
  'SecurityEvents.Read.All',
  'DeviceManagementManagedDevices.Read.All',
  'SharePointTenantSettings.Read.All',
  // Intune configuration checks (0182): DeviceManagementConfiguration.Read.All requested again.
  'DeviceManagementConfiguration.Read.All',
  'DeviceManagementApps.Read.All',
  // Entra device registration and access reviews (0191).
  'Policy.Read.DeviceConfiguration',
  'AccessReview.Read.All',
]

export interface AssessedService {
  name: string
  /** What is read there, in a few words. Never wider than the registered checks. */
  reads: string
  /**
   * True where Microsoft's consent is not enough on its own: Exchange Online, and Defender for
   * Office 365, whose policies are read through Exchange. Both need a directory role assigned to
   * Defenssive's application after consent -- see ExchangeRoleSteps.
   */
  needsExchangeRole?: boolean
}

/**
 * What Defenssive assesses, service by service, shown on the Connect screen before consent.
 *
 * THIS LIST WAS A PROMISE THE PRODUCT DID NOT KEEP. It named Microsoft Purview (whose checks are
 * written but not registered), Microsoft Sentinel and "Azure security configuration" (neither is
 * read at all), called the whole Email area "Microsoft 365", and said nothing about the one real
 * extra step -- the Exchange directory role. It is now the registered checks, by service, worded
 * no wider than they read:
 *  - Entra ID: the Identity area (SCuBA MS.AAD and the additional identity checks).
 *  - Exchange Online: MS.EXO forwarding, sharing, SMTP AUTH, mail flow rules, SPF/DKIM/DMARC, and the
 *    anti-phishing, anti-malware and anti-spam policies in effect (0185), and modern authentication,
 *    MailTips, Outlook on the web and role assignment policies (0186).
 *  - Defender for Office 365: the preset security policies (MS.DEFENDER.1.x) and the settings read
 *    beside them, and the Safe Links and Safe Attachments policies in effect (0185). Not its alerts,
 *    which are Purview's.
 *  - Defender for Endpoint: the five registered checks -- sensor health, antivirus health, Intune
 *    devices with no Defender record (DefenderCoverageCheck), Microsoft's configuration
 *    recommendations (DefenderMisconfigurationCheck) and vulnerabilities with a known exploit
 *    (DefenderExploitableVulnerabilityCheck) -- plus what the scan records beside them (0153): the
 *    whole vulnerability list, counted, and the exposure score. It said "sensor and antivirus health"
 *    alone, which left out the most sensitive reads on the screen whose job is naming them.
 *  - Intune: device compliance.
 *  - SharePoint and OneDrive: tenant-wide settings for sharing, legacy sign-in, idle sign-out and sync (0180).
 *  - Microsoft Teams: client, meeting and messaging policy settings (0189) and external access (0190). Not flagged needsExchangeRole: it needs
 *    the directory role, but only Global Reader is proven for it, and the role step offers Security Reader too.
 * Each line is a summary of its service, not every check in it, so the Connect screen's footnote
 * speaks of services not listed, not of "anything else". A check added in a new service is not
 * covered here until this list says so, and a new kind of read in a listed service (as Defender's
 * vulnerabilities were) belongs in that service's line.
 */
export const WILL_ASSESS: AssessedService[] = [
  {
    name: 'Microsoft Entra ID',
    reads:
      'sign-in and MFA, Conditional Access, privileged roles, password protection, idle sign-out, ' +
      'device join settings and access reviews',
  },
  {
    name: 'Exchange Online',
    reads: 'forwarding, sharing, SMTP AUTH, mail flow rules, anti-phishing, anti-malware and anti-spam policies, modern authentication, Outlook add-ins and personal accounts, SPF, DKIM and DMARC',
    needsExchangeRole: true,
  },
  {
    name: 'Microsoft Defender for Office 365',
    reads: 'the preset security policies, Safe Links and Safe Attachments',
    needsExchangeRole: true,
  },
  {
    name: 'Microsoft Defender for Endpoint',
    reads:
      'sensor and antivirus health, Intune devices with no Defender record, configuration ' +
      'recommendations, software vulnerabilities (a finding only where an exploit is known) and ' +
      'the exposure score',
  },
  { name: 'Microsoft Intune', reads: 'device compliance, compliance policies, endpoint security policies, Windows Update rings and app protection' },
  { name: 'SharePoint and OneDrive', reads: 'tenant-wide sharing, sign-in and sync settings' },
  {
    name: 'Microsoft Teams',
    reads:
      'external access, email into channels, outside cloud storage, anonymous join and the meeting lobby, meeting chat, ' +
      'who can present and take control, and reporting suspicious messages (needs Global Reader, from the ' +
      'role step below)',
  },
  {
    // Azure CSPM (2026-10-10): open-source Azure checks, Monitor plan only, on the subscriptions the
    // customer gives Reader. Not needsExchangeRole: it needs an Azure role, which Settings > Azure
    // explains after connecting, not the directory role step on this screen.
    name: 'Microsoft Azure (Monitor plan)',
    reads:
      'the configuration of each subscription where you give our application the Reader role, such as ' +
      'storage, networking, virtual machines, databases, Key Vault and Defender for Cloud, assessed about ' +
      'once a week',
  },
]

/**
 * What Defenssive does not assess, named, so nobody has to infer it from an absence.
 *
 * Each is narrowed to what is really not read. Purview is "data loss prevention, labels and
 * retention" because the unified audit log setting (MS.DEFENDER.6.1) IS read, through Exchange.
 * Teams is no longer listed: its settings are read (0189). Nor is Azure (2026-10-10): it is read on
 * the Monitor plan, where the customer gives Reader, and the WILL_ASSESS line says both conditions.
 */
export const WILL_NOT_ASSESS = [
  'Microsoft Purview: data loss prevention, sensitivity labels, retention',
  'Microsoft Sentinel',
  'Power Platform',
]

/**
 * The tenant-wide admin consent URL, as a labelled placeholder.
 *
 * The real one carries the customer's tenant id and cannot be built until Entra has identified
 * them, so the prototype must not present a URL that looks real enough to send to an
 * administrator.
 */
export const PLACEHOLDER_CONSENT_URL =
  'https://login.microsoftonline.com/{tenant-id}/adminconsent?client_id={client-id}'

/**
 * What Defenssive will not do, in the published privacy notice's words where it has them.
 *
 * The last line used to be "Modify security settings without authorization", which says that with
 * authorization it would. It makes no write call of any kind, so the line now says that.
 */
export const WILL_NOT = [
  'Change any setting in your tenant, including the ones we report on',
  'Read the contents of mailboxes, files, chats or calendars',
  'Send mail as anyone in your organization',
  'Read or store anyone’s password',
  'Access personal Microsoft accounts',
]

export interface ChecklistTask {
  key: string
  label: string
  doneByDefault: boolean
}

export const CHECKLIST: ChecklistTask[] = [
  { key: 'account', label: 'Create account', doneByDefault: true },
  { key: 'tenant', label: 'Connect Microsoft tenant', doneByDefault: true },
  { key: 'assess', label: 'Complete security assessment', doneByDefault: true },
  { key: 'risks', label: 'Review critical risks', doneByDefault: false },
  { key: 'identity', label: 'Review identity security', doneByDefault: false },
  { key: 'endpoint', label: 'Review endpoint security', doneByDefault: false },
  { key: 'compliance', label: 'Review compliance', doneByDefault: false },
  { key: 'report', label: 'Generate executive report', doneByDefault: false },
  { key: 'plan', label: 'Create remediation plan', doneByDefault: false },
]

export const TOP_FIXES = [
  'Enforce MFA',
  'Protect privileged accounts',
  'Improve Conditional Access',
  'Address high-risk endpoints',
  'Enable additional Defender protections',
]

export const DOMAIN_SCORES = [
  { label: 'Identity', value: 72 },
  { label: 'Endpoint', value: 61 },
  { label: 'Email', value: 79 },
]

/**
 * Control frameworks, as counts of mapped findings — NOT completion percentages.
 *
 * The design shows "CIS 68%", "NIST 71%", "SOC 2 64%". Three problems, and only the first is
 * about CIS. A percentage needs a versioned denominator, an applicability decision, a
 * pass/fail/not-assessed state per control and evidence behind each; a catalogue of mapped
 * control ids supports none of that. SOC 2 cannot carry a percentage at all — it is an
 * independent attestation, not something a configuration scan produces. And CIS is licensed
 * content the product holds no licence for.
 *
 * So this shows what is true: how many findings carry a mapping to each framework. See
 * PLAN.md's design-copy corrections and ADR 0006.
 */
export const FRAMEWORK_COVERAGE = [
  { label: 'NIST 800-53', mapped: 9, note: 'Control ids on mapped findings' },
  { label: 'NIST AI RMF', mapped: 1, note: 'Control ids on mapped findings' },
  { label: 'SOC 2', mapped: 5, note: 'Informational mapping — not an attestation' },
]

/** Fixture tenant facts, shown once the connection verifies. */
export const TENANT_FACTS = {
  tenantIdMasked: '7f3c9ae2 · 4b81 · 4e0d',
  users: 87,
  devices: 104,
  services: 6,
}

export const DEFAULT_SCORE = 68

export const SEVERITY_TOTALS = {
  critical: 7,
  high: 14,
  medium: 23,
  good: 41,
}

export const AI_SUMMARY =
  '“Your highest-risk issue is identity security. 12 users currently do not have MFA ' +
  'enforcement through Conditional Access.”'

export const DEFAULT_COMPANY = 'ABC Manufacturing'
export const DEFAULT_ADMIN_EMAIL = 'john@abc.com'
